import React, { useCallback, useContext, useState } from 'react';
import { ExecutivePortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider/ExecutivePortalsProvider';
import useAdminPortal from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider/useAdminPortal';

import PortalOption from './PortalOption';
import PortalSearchInput from './PortalSearchInput';
import CreatePortalButton from './CreatePortalButton';
import { OPENED_NAV_WIDTH } from '../../../navigation/styles';
import Row from '../../../components/Common/Row';
import Typography from '../../atoms/Typography';
import Button from '../../atoms/Button';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { EngagementPortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider/EngagementPortalsProvider';

const MIN_WIDTH = OPENED_NAV_WIDTH - 18;

const AdminPortalLink = ({ close }: { close: () => void }) => {
  const { onPortalSelected } = useContext(PortalsContext);

  const adminPortal = useAdminPortal();

  const onClick = useCallback(() => {
    if (!adminPortal) {
      return;
    }
    onPortalSelected(adminPortal);
    close();
  }, [adminPortal, close, onPortalSelected]);

  if (!adminPortal) {
    return null;
  }

  return (
    <Button
      size={'Small'}
      type={'Ghost'}
      onClick={onClick}
      label="Admin"
      iconAfter="settings-filled"
    />
  );
};

const PortalPickerDialog = ({
  close,
  openPortalForm,
}: {
  close: () => void;
  openPortalForm: () => void;
}) => {
  const { availableExecutivePortals } = useContext(ExecutivePortalsContext);
  const { availableEngagementPortals } = useContext(EngagementPortalsContext);

  const [searchText, setSearchText] = useState('');
  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const hasNoPortals =
    availableExecutivePortals.length === 0 &&
    availableEngagementPortals.length === 0;

  return (
    <div
      style={{
        minWidth: MIN_WIDTH,
      }}
    >
      <Row centerAlign spaceBetween style={{ padding: '8px 8px 0px 8px' }}>
        <Typography.Body type="Body Bold">Select A Portal</Typography.Body>
        <AdminPortalLink close={close} />
      </Row>
      <PortalSearchInput
        searchText={searchText}
        onSearchTextChanged={onSearchTextChanged}
      />
      <div style={{ maxHeight: 256, overflowY: 'scroll', marginTop: 8 }}>
        {availableExecutivePortals.length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
            <div
              style={{
                paddingLeft: '8px',
                borderBottom: '1px solid var(--color-border-secondary)',
                height: 34,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography.Body
                type="Button Text"
                color="var(--color-icon-secondary)"
              >
                Executive
              </Typography.Body>
            </div>
            {availableExecutivePortals.map((portal) => (
              <PortalOption portal={portal} key={portal.id} close={close} />
            ))}
          </div>
        )}
        {availableEngagementPortals.length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
            <div
              style={{
                paddingLeft: '8px',
                borderBottom: '1px solid var(--color-border-secondary)',
                height: 34,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography.Body
                type="Button Text"
                color="var(--color-icon-secondary)"
              >
                Engagement
              </Typography.Body>
            </div>
            {availableEngagementPortals.map((portal) => (
              <PortalOption portal={portal} key={portal.id} close={close} />
            ))}
          </div>
        )}
        {hasNoPortals && (
          <div
            style={{
              borderTop: '1px solid var(--color-border-secondary)',
              width: MIN_WIDTH,
              padding: 8,
            }}
          >
            <Typography.Body type="Body Bold">
              No portals have been assigned to you yet.
            </Typography.Body>
          </div>
        )}
      </div>
      <CreatePortalButton
        closePortalPicker={close}
        openPortalForm={openPortalForm}
      />
    </div>
  );
};

export default PortalPickerDialog;
